import React, { useState, useEffect } from 'react';
import { Globe, Menu, X, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteContent, setSiteContent] = useState(null);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchSiteContent();
  }, []);

  const fetchSiteContent = async () => {
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const response = await axios.get(`${API}/site-content`);
      setSiteContent(response.data);
    } catch (error) {
      console.error('Error fetching site content:', error);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const phone = siteContent?.contact_phone || t('nav.phone');

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/gallery', label: t('nav.collection') },
    { path: '/about', label: t('nav.about') },
    { path: '/order', label: t('nav.order') },
    { path: '/reviews', label: t('nav.reviews') },
    { path: '/contacts', label: t('nav.contacts') },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 md:py-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex flex-col flex-shrink-0" data-testid="logo-link">
            <span className="font-heading text-xl sm:text-2xl xl:text-3xl tracking-tight text-foreground">
              Pure Atelier Riga
            </span>
            <span className="font-body text-[8px] sm:text-[10px] xl:text-xs tracking-widest uppercase text-accent mt-1">
              custom fine jewelry & engagement rings
            </span>
          </Link>

          <div className="hidden xl:flex items-center gap-6 2xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-body text-sm uppercase tracking-wider relative group whitespace-nowrap ${
                  location.pathname === link.path ? 'text-foreground' : 'text-muted-foreground'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-foreground transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
            <a href={`tel:${phone}`} className="flex items-center gap-2 text-sm uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap" data-testid="phone-link">
              <Phone className="w-4 h-4" />
              <span>{phone}</span>
            </a>
            
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center space-x-1 text-sm uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors" data-testid="language-selector">
                <Globe className="w-4 h-4" />
                <span>{i18n.language.toUpperCase()}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => changeLanguage('ru')} data-testid="lang-ru">
                  Русский
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('lv')} data-testid="lang-lv">
                  Latviešu
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <button
            className="xl:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-background border-t border-border"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block font-body text-sm uppercase tracking-widest"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <a href={`tel:${phone}`} className="flex items-center space-x-2 text-sm uppercase tracking-widest" data-testid="mobile-phone-link">
                <Phone className="w-4 h-4" />
                <span>{phone}</span>
              </a>
              <div className="flex space-x-4">
                <button onClick={() => changeLanguage('ru')} className="text-sm uppercase tracking-widest">RU</button>
                <button onClick={() => changeLanguage('lv')} className="text-sm uppercase tracking-widest">LV</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
